
import React, { useEffect, useState, useMemo, useCallback } from "react";
import api from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";
import VehicleForm from "../components/VehicleForm";
import VehicleLogList from "../components/VehicleLogList";
import { XCircle, RefreshCcw, ChevronDown, Edit, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import MultiSelectCombobox from "../components/ui/MultiSelectCombobox";
import * as XLSX from 'xlsx';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { useToast } from "../components/ui/use-toast";

export default function VehicleList() {
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVehicle, setEditingVehicle] = useState(null);
  
  // Cache last fetch time to prevent unnecessary re-fetching
  const [lastFetchTime, setLastFetchTime] = useState(0);
  
  // Column sorting state
  const [sortColumn, setSortColumn] = useState('machinery_no');
  const [sortDirection, setSortDirection] = useState('none'); // 'asc' | 'desc' | 'none'
  const [adding, setAdding] = useState(false);
  const [logs, setLogs] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [exportRange, setExportRange] = useState({ from: "", to: "" });
  const statusOptions = [
    { value: "available", label: "Available" },
    { value: "in_use", label: "In Use" },
    { value: "in_maintenance", label: "In Maintenance" },
    { value: "no_longer_available", label: "No Longer Available" },
  ];
  const [carTypeFilter, setCarTypeFilter] = useState("");
  const [deployments, setDeployments] = useState([]);
  const [deploymentFilter, setDeploymentFilter] = useState("");
  const [useDeploymentRange, setUseDeploymentRange] = useState(false);
  const [plateNumberFilter, setPlateNumberFilter] = useState([]);
  const [machineryFilter, setMachineryFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);

  // Optimized filter options with useMemo and proper sorting
  const carTypeOptions = useMemo(() => {
    const types = [...new Set(vehicles.map((v) => v.car_type))].filter(Boolean);
    return types.sort().map((type) => ({
      value: type,
      label: type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
    }));
  }, [vehicles]);

  const deploymentOptions = useMemo(() => {
    return deployments
      .map((d) => ({
        value: String(d.id),
        label: d.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [deployments]);

  const plateNumberOptions = useMemo(() => {
    const plates = [...new Set(vehicles.map((v) => v.plate_number))].filter(Boolean);
    return plates.sort().map((p) => ({
      value: p,
      label: p,
    }));
  }, [vehicles]);

  const machineryOptions = useMemo(() => {
    const machinery = [...new Set(vehicles.map((v) => v.machinery_no).filter(Boolean))];
    // Sort numerically for machinery numbers
    return machinery
      .sort((a, b) => {
        const numA = parseInt(a) || 0;
        const numB = parseInt(b) || 0;
        return numA - numB;
      })
      .map((m) => ({
        value: m,
        label: m,
      }));
  }, [vehicles]);

  const companyOptions = useMemo(() => {
    const companies = [...new Set(vehicles.map((v) => v.company_name))].filter(Boolean);
    return companies
      .sort()
      .map((company) => ({
        value: company,
        label: company.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      }));
  }, [vehicles]);
  const [roadStatusFilter, setRoadStatusFilter] = useState([]);
  const roadStatusOptions = [
    { value: "on_road", label: "On Road" },
    { value: "off_road", label: "Off Road" },
  ];
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    insurance: false,
    roadtax: false,
    inspection: false,
    brunei: false,
    sabah: false,
    sarawak: false,
  });

  const { isAdmin, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const visibleKeys = Object.entries(visibleColumns).filter(([_, val]) => val).map(([key]) => key);
  const columnCount = 8 + visibleKeys.length; // plate number, machinery no., type, manufacturer, model, status, company, deployment + toggled ones
  const gridTemplate = `repeat(${columnCount}, minmax(0, 1fr))`;

  // Advanced sorting with column sorting support
  const sortedVehicles = useMemo(() => {
    if (sortDirection === 'none') {
      // Default sort by expiry dates when no column sorting
      return [...filteredVehicles].sort((a, b) => {
        const parse = (d) => (d ? new Date(d) : null);

        const getEarliest = (vehicle) => {
          const dates = [
            parse(vehicle.insurance?.expiry_date),
            parse(vehicle.roadtax?.expiry_date),
            parse(vehicle.inspection?.expiry_date),
            ...vehicle.permits?.map(p => parse(p.expiry_date)) || [],
          ].filter(Boolean);
          return dates.length ? Math.min(...dates.map(d => d.getTime())) : Infinity;
        };

        return getEarliest(a) - getEarliest(b);
      });
    }

    // Column-based sorting
    return [...filteredVehicles].sort((a, b) => {
      let aValue, bValue;

      // Handle special cases
      if (sortColumn === 'machinery_no') {
        aValue = parseInt(a[sortColumn]) || 0;
        bValue = parseInt(b[sortColumn]) || 0;
      } else if (sortColumn === 'car_type') {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      } else if (sortColumn === 'company_name') {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      } else if (sortColumn === 'project') {
        aValue = a.project?.name || '';
        bValue = b.project?.name || '';
      } else if (sortColumn === 'insurance') {
        aValue = a.insurance?.expiry_date || '';
        bValue = b.insurance?.expiry_date || '';
      } else if (sortColumn === 'roadtax') {
        aValue = a.roadtax?.expiry_date || '';
        bValue = b.roadtax?.expiry_date || '';
      } else if (sortColumn === 'inspection') {
        aValue = a.inspection?.expiry_date || '';
        bValue = b.inspection?.expiry_date || '';
      } else if (sortColumn === 'brunei') {
        aValue = a.permits?.find(p => p.permit_type === 'brunei')?.expiry_date || '';
        bValue = b.permits?.find(p => p.permit_type === 'brunei')?.expiry_date || '';
      } else if (sortColumn === 'sabah') {
        aValue = a.permits?.find(p => p.permit_type === 'sabah')?.expiry_date || '';
        bValue = b.permits?.find(p => p.permit_type === 'sabah')?.expiry_date || '';
      } else if (sortColumn === 'sarawak') {
        aValue = a.permits?.find(p => p.permit_type === 'sarawak')?.expiry_date || '';
        bValue = b.permits?.find(p => p.permit_type === 'sarawak')?.expiry_date || '';
      } else {
        aValue = a[sortColumn];
        bValue = b[sortColumn];
      }

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

        // Date comparison for document expiry dates
        if (sortColumn === 'insurance' || sortColumn === 'roadtax' || sortColumn === 'inspection' || 
            sortColumn === 'brunei' || sortColumn === 'sabah' || sortColumn === 'sarawak') {
          const aDate = aValue ? new Date(aValue) : null;
          const bDate = bValue ? new Date(bValue) : null;
          
          // Handle missing dates (put them at the end)
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          
          const comparison = aDate.getTime() - bDate.getTime();
          return sortDirection === 'asc' ? comparison : -comparison;
        }

        // String comparison for car_type and company_name
        if (sortColumn === 'car_type' || sortColumn === 'company_name') {
          const comparison = aValue.localeCompare(bValue);
          return sortDirection === 'asc' ? comparison : -comparison;
        }

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Numeric comparison
      const comparison = aValue - bValue;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredVehicles, sortColumn, sortDirection]);
  const extraFieldsList = [
    { key: "car_type", label: "Vehicle Type" },
    { key: "manufacturer", label: "Manufacturer" },
    { key: "model", label: "Model" },
    { key: "year_of_manufacture", label: "Year of Manufacture" },
    { key: "engine_chassis", label: "Engine / Chassis" },
    { key: "machinery_no", label: "Machinery No." },
    { key: "bdm", label: "BDM" },
    { key: "btm", label: "BTM" },
    { key: "bg2", label: "BG2" },
    { key: "company_name", label: "Company" },
    { key: "status", label: "Status" },
    { key: "road_status", label: "Road Status" },
    { key: "project", label: "Deployment" },
  ];

  const [showExportDialog, setShowExportDialog] = useState(false); // ⭐ ADDED
  const [selectedExtras, setSelectedExtras] = useState([]);       // ⭐ ADDED
  const [dialogColumns, setDialogColumns] = useState({
    ...visibleColumns,
    deployment_project: true,
    deployment_date_in: true,
    deployment_date_out: true,
    deployment_remarks: true,
  });

  // Quick update dialog state
  const [showQuickUpdateDialog, setShowQuickUpdateDialog] = useState(false);
  const [quickUpdateVehicle, setQuickUpdateVehicle] = useState(null);
  const [quickUpdateData, setQuickUpdateData] = useState({
    status: "",
    project: ""
  });

  // Quick update function
  const handleQuickUpdate = async () => {
    if (!quickUpdateVehicle || (!quickUpdateData.status && !quickUpdateData.project)) {
      toast({
        title: "Validation Error",
        description: "Please select at least one field to update.",
        variant: "destructive",
      });
      return;
    }

    try {
      const updatePayload = {};
      if (quickUpdateData.status) updatePayload.status = quickUpdateData.status;
      if (quickUpdateData.project) updatePayload.project_id = quickUpdateData.project;



      await api.patch(
        `/api/vehicles/${quickUpdateVehicle.id}/`,
        updatePayload
      );

      // Update local state
      setVehicles(prev => prev.map(v =>
        v.id === quickUpdateVehicle.id
          ? {
              ...v,
              ...updatePayload,
              project: quickUpdateData.project
                ? deployments.find(d => d.id === parseInt(quickUpdateData.project))
                : v.project
            }
          : v
      ));

      toast({
        title: "Success",
        description: "Vehicle updated successfully!",
      });

      setShowQuickUpdateDialog(false);
      setQuickUpdateVehicle(null);
      setQuickUpdateData({ status: "", project: "" });
    } catch (error) {
      console.error("Quick update failed:", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to update vehicle.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const openQuickUpdateDialog = (vehicle) => {
    setQuickUpdateVehicle(vehicle);
    setQuickUpdateData({
      status: vehicle.status || "",
      project: vehicle.project?.id?.toString() || ""
    });
    setShowQuickUpdateDialog(true);
  };
  // ⭐ UPDATED generateCSV
  const generateXLSX = async () => {
    try {
      // ✅ Fetch all deployments
      const deploymentsRes = await api.get(`/api/deployments/`);
      const allDeployments = deploymentsRes.data;

      // ✅ Build a quick map of filtered vehicles
      const vehicleMap = {};
      filteredVehicles.forEach((v) => {
        vehicleMap[v.id] = v;
      });

      // ✅ Prepare deployment records only if checkbox is ticked
      let filteredDeployments = [];
      if (useDeploymentRange) {
        filteredDeployments = allDeployments.filter((rec) => {
          if (!vehicleMap[rec.vehicle]) return false;
          const d = rec.date_in ? new Date(rec.date_in) : null;
          const from = exportRange.from ? new Date(exportRange.from) : null;
          const to = exportRange.to ? new Date(exportRange.to) : null;
          if (from && d && d < from) return false;
          if (to && d && d > to) return false;
          return true;
        });
      }

      // ✅ Prepare headers with documents always on the right
      let headers = ["Plate Number"]; // Always include plate number
      
      // Add extra vehicle info fields first (left side)
      const extraHeaders = selectedExtras.map(
          (key) => extraFieldsList.find((f) => f.key === key)?.label || key
      );
      headers = [...headers, ...extraHeaders];
      
      // Add deployment headers if checkbox is ticked
      if (useDeploymentRange) {
        headers.push("Deployment Project", "Date In", "Date Out", "Remarks");
      }
      
      // Add document columns last (rightmost side)
      if (dialogColumns.insurance) headers.push("Insurance");
      if (dialogColumns.roadtax) headers.push("Roadtax");
      if (dialogColumns.inspection) headers.push("Inspection");
      if (dialogColumns.brunei) headers.push("Brunei Permit", "Brunei Ref No");
      if (dialogColumns.sabah) headers.push("Sabah Permit", "Sabah Ref No");
      if (dialogColumns.sarawak) headers.push("Sarawak Permit", "Sarawak Ref No");

      // ✅ Build rows
      const rows = [];
      const vehiclesWithDeployments = new Set();

      // Helper function to get permit data with reference number
      const getPermitData = (v, type) => {
        const permit = v.permits?.find((p) => p.permit_type === type);
        if (!permit) return ["-", "-"];
        return [permit.expiry_date || "-", permit.license_number || "-"];
      };
      
      // Function to build document columns (always on the right)
      const buildDocColumns = (v) => {
        const docCols = [];
        if (dialogColumns.insurance) docCols.push(v.insurance?.expiry_date || "-");
        if (dialogColumns.roadtax) docCols.push(v.roadtax?.expiry_date || "-");
        if (dialogColumns.inspection) docCols.push(v.inspection?.expiry_date || "-");
        
        if (dialogColumns.brunei) {
          const [expiry, refNo] = getPermitData(v, "brunei");
          docCols.push(expiry, refNo);
        }
        if (dialogColumns.sabah) {
          const [expiry, refNo] = getPermitData(v, "sabah");
          docCols.push(expiry, refNo);
        }
        if (dialogColumns.sarawak) {
          const [expiry, refNo] = getPermitData(v, "sarawak");
          docCols.push(expiry, refNo);
        }
        return docCols;
      };

      const buildExtraCols = (v) => selectedExtras.map((key) => {
        if (key === "project") return v.project?.name || "-";
        if (["car_type", "status", "road_status", "company_name"].includes(key)) {
          return formatLabel(v[key]) || "-";
        }
        return v[key] || "-";
      });

      if (useDeploymentRange) {
        // ✅ Include deployment rows filtered by date
        filteredDeployments.forEach((rec) => {
          const v = vehicleMap[rec.vehicle];
          if (!v) return;
          vehiclesWithDeployments.add(v.id);

          const baseCols = [v.plate_number];
          const extraCols = buildExtraCols(v);
          const docCols = buildDocColumns(v);

          rows.push([
            ...baseCols,                    // Plate Number
            ...extraCols,                   // Extra Vehicle Info
            // deployment columns
            deployments.find((p) => p.id === rec.project)?.name || "-",
            rec.date_in || "-",
            rec.date_out || "-",
            rec.remarks || "-",
            ...docCols                      // Document columns (always on the right)
          ]);
        });
      }

      // ✅ Add vehicles with no deployments (only base columns if not using deployment)
      filteredVehicles.forEach((v) => {
        if (useDeploymentRange && vehiclesWithDeployments.has(v.id)) return;

        const baseCols = [v.plate_number];
        const extraCols = buildExtraCols(v);
        const docCols = buildDocColumns(v);

        rows.push([
          ...baseCols,                    // Plate Number
          ...extraCols,                   // Extra Vehicle Info
          ...(useDeploymentRange ? ["-", "-", "-", "-"] : []), // only add placeholders if we are including deployment columns
          ...docCols                      // Document columns (always on the right)
        ]);
      });

      // ✅ Build worksheet from headers & rows
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // ✅ Optional: Set column widths
      worksheet["!cols"] = headers.map((h) => {
        if (h.includes("Date")) return { wch: 12 };
        if (h.includes("Remarks")) return { wch: 15 };
        return { wch: 15 };
      });

      // ✅ Create workbook and trigger download
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicles");
      XLSX.writeFile(workbook, "VehicleList.xlsx");
    } catch (err) {
      console.error("Error generating XLSX:", err);
      alert("Failed to export XLSX.");
    }
  };

  const fetchAllDeployments = async () => {
    const res = await api.get(`/api/deployments/`);
    return res.data;
  };
  const isWithinRange = (record, from, to) => {
    const d = new Date(record.date_in); // or date_out if you prefer
    if (from && d < new Date(from)) return false;
    if (to && d > new Date(to)) return false;
    return true;
  };

  function formatLabel(value) {
    if (!value) return "-";
    return value
        .split("_")               // split by underscore
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize
        .join(" ");               // join with space
  }
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Column sort handler
  const handleColumnSort = useCallback((column) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> none -> asc
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection('none');
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  // Helper function to get sort icon
  const getSortIcon = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        return <ArrowUp size={14} />;
      } else if (sortDirection === 'desc') {
        return <ArrowDown size={14} />;
      } else {
        return <ArrowUpDown size={14} />;
      }
    } else {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
  };

  const applyFilters = useCallback((list) => {
    let result = [...list];

    // Apply search filter first (most restrictive)
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter((v) =>
        v.plate_number?.toLowerCase().includes(searchLower) ||
        v.machinery_no?.toLowerCase().includes(searchLower) ||
        v.car_type?.toLowerCase().includes(searchLower) ||
        v.manufacturer?.toLowerCase().includes(searchLower) ||
        v.model?.toLowerCase().includes(searchLower) ||
        v.company_name?.toLowerCase().includes(searchLower) ||
        v.project?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply other filters
    if (statusFilter.length) {
      result = result.filter((v) => statusFilter.includes(v.status));
    }
    if (carTypeFilter.length) {
      result = result.filter((v) => carTypeFilter.includes(v.car_type));
    }
    if (deploymentFilter && deploymentFilter.length) {
      result = result.filter((v) =>
          deploymentFilter.includes(String(v.project?.id))
      );
    }
    if (plateNumberFilter.length) {
      result = result.filter((v) => plateNumberFilter.includes(v.plate_number));
    }
    if (companyFilter.length) {
      result = result.filter((v) => companyFilter.includes(v.company_name));
    }
    if (machineryFilter.length) {
      result = result.filter((v) => machineryFilter.includes(v.machinery_no));
    }
    if (roadStatusFilter.length) {
      result = result.filter((v) => roadStatusFilter.includes(v.road_status));
    }

    // Debug logging
    console.log('applyFilters:', {
      originalCount: list.length,
      filteredCount: result.length,
      filters: {
        search,
        statusFilter,
        carTypeFilter,
        deploymentFilter,
        plateNumberFilter,
        companyFilter,
        machineryFilter,
        roadStatusFilter
      }
    });

    setFilteredVehicles(result);
  }, [search, statusFilter, carTypeFilter, deploymentFilter, plateNumberFilter, companyFilter, machineryFilter, roadStatusFilter]);

  // Optimized data loading with parallel API calls and caching
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Check if we need to fetch data (avoid unnecessary re-fetching)
    const now = Date.now();
    const cacheExpiry = 30000; // 30 seconds cache
    
    if (vehicles.length > 0 && deployments.length > 0 && (now - lastFetchTime) < cacheExpiry) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Performance monitoring
    const startTime = performance.now();
    
    // Load all data in parallel for faster initial load
    Promise.all([
      api.get("/api/vehicles/"),
      api.get("/api/projects/")
    ])
    .then(([vehiclesRes, projectsRes]) => {
      const endTime = performance.now();
      console.log(`Data loaded in ${(endTime - startTime).toFixed(2)}ms`);
      
      setVehicles(vehiclesRes.data);
      setDeployments(projectsRes.data || []);
      setLastFetchTime(now);
      applyFilters(vehiclesRes.data);
    })
    .catch((error) => {
      console.error("Failed to load data:", error);
      alert("Failed to load data. Please refresh the page.");
    })
    .finally(() => setLoading(false));
  }, [isAuthenticated, applyFilters, vehicles.length, deployments.length, lastFetchTime]);

  useEffect(() => {
    applyFilters(vehicles);
  }, [applyFilters, vehicles]);

  const reloadVehicles = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/api/vehicles/"),
      api.get("/api/projects/")
    ])
    .then(([vehiclesRes, projectsRes]) => {
      setVehicles(vehiclesRes.data);
      setDeployments(projectsRes.data || []);
      setLastFetchTime(Date.now());
      applyFilters(vehiclesRes.data);
    })
    .catch((error) => {
      console.error("Failed to reload data:", error);
      alert("Failed to reload data.");
    })
    .finally(() => {
      setLoading(false);
      setAdding(false);
      setEditingVehicle(null);
    });
  }, [applyFilters]);

  const handleDelete = (id) => {
    if (window.confirm("Are you sure to delete this vehicle?")) {
      api
          .delete(`/api/vehicles/${id}/`)
          .then(() => reloadVehicles())
          .catch(() => alert("Failed to delete vehicle."));
    }
  };

  const handleViewLogs = (vehicleId) => {
    api
        .get(`/api/vehicle-history/?vehicle_id=${vehicleId}`)
        .then((res) => {
          setLogs((prev) => ({ ...prev, [vehicleId]: res.data }));
        })
        .catch(() => alert("Failed to fetch logs"));
  };

  return (
      <div className="space-y-6 w-full max-w-full lg:max-w-7xl xl:max-w-full mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center">
          {/* Left side: Title */}
          <h2 className="text-2xl font-bold">Vehicle List</h2>

          {/* Right side: Documentation + Export */}
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  Documentation <ChevronDown size={16} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 space-y-2 bg-[#f5f5f5] text-black shadow-lg border border-gray-200 p-2 rounded-md">
                {Object.entries(visibleColumns).map(([key, val]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                          id={key}
                          checked={val}
                          onCheckedChange={() => toggleColumn(key)}
                      />
                      <label htmlFor={key} className="capitalize text-sm">
                        {key}
                      </label>
                    </div>
                ))}
              </PopoverContent>
            </Popover>

            <Button
                variant="outline"
                onClick={() => {
                  setDialogColumns({ ...visibleColumns }); // snapshot of current visibleColumns
                  setShowExportDialog(true);
                }}
            >
              Export to Excel
            </Button>
          </div>
        </div>

        <div className="flex flex-row flex-wrap items-center gap-4">
          <div className="w-48">
            <MultiSelectCombobox
                options={plateNumberOptions}
                selected={plateNumberFilter}
                onChange={setPlateNumberFilter}
                placeholder="Filter by Plate Number"
                className="w-full border rounded-md bg-white hover:bg-gray-50 text-sm py-2"
            />
          </div>

          <div className="w-48">
            <MultiSelectCombobox
                options={machineryOptions}
                selected={machineryFilter}
                onChange={setMachineryFilter}
                placeholder="Filter by Machinery No."
                className="w-full border rounded-md bg-white hover:bg-gray-50 text-sm py-2"
            />
          </div>

          <div className="w-48">
            <MultiSelectCombobox
                options={statusOptions}
                selected={statusFilter}
                onChange={setStatusFilter}
                placeholder="Filter by Status"
            />
          </div>

          <div className="w-48">
            <MultiSelectCombobox
                options={carTypeOptions}
                selected={carTypeFilter}
                onChange={setCarTypeFilter}
                placeholder="Filter by Vehicle Type"
            />
          </div>

          <div className="w-48">
            <MultiSelectCombobox
                options={companyOptions}
                selected={companyFilter}
                onChange={setCompanyFilter}
                placeholder="Filter by Company"
            />
          </div>
          <div className="w-48">
            <MultiSelectCombobox
                options={deploymentOptions}
                selected={deploymentFilter}
                onChange={setDeploymentFilter}
                placeholder="Filter by Deployment"
            />
          </div>

          <div className="w-48">
            <MultiSelectCombobox
                options={roadStatusOptions}
                selected={roadStatusFilter}
                onChange={setRoadStatusFilter}
                placeholder="Filter by Road Status"
            />
          </div>

          <Button
              variant="outline"
              onClick={() => {
                setSearch("");
                setStatusFilter([]);
                setCarTypeFilter([]);
                setDeploymentFilter([]);
                setPlateNumberFilter([]);
                setMachineryFilter([]);
                setCompanyFilter([]);
                setRoadStatusFilter([]);
              }}

          >
            <RefreshCcw size={16} className="mr-2" />
            Reset
          </Button>

          {vehicles.length > 0 && (statusFilter || carTypeFilter || deploymentFilter || search || roadStatusFilter.length || plateNumberFilter.length || machineryFilter.length || companyFilter.length) && (
              <div className="text-sm text-gray-600 ml-1">
                Showing {filteredVehicles.length} of {vehicles.length} vehicles
              </div>
          )}
        </div>

        <div className="border rounded-md mt-4 overflow-x-auto">
          <div className="min-w-max">
      {loading ? (
        <div className="p-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid gap-4" style={{ gridTemplateColumns: gridTemplate }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="grid gap-4 py-2" style={{ gridTemplateColumns: gridTemplate }}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <div key={j} className="h-6 bg-gray-200 rounded"></div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
            <>
          <div
              className="font-semibold bg-gray-100 p-3 border-b text-sm"
              style={{ display: "grid", gridTemplateColumns: `40px ${gridTemplate}`, gap: "8px" }}
          >
            <div className="text-center text-gray-500 flex items-center justify-center p-1">#</div>
            <div className="flex items-center p-1">Plate Number</div>
            <div 
              className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
              onClick={() => handleColumnSort('machinery_no')}
            >
              Machinery No.
              {getSortIcon('machinery_no')}
            </div>
            <div 
              className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
              onClick={() => handleColumnSort('car_type')}
            >
              Vehicle Type
              {getSortIcon('car_type')}
            </div>
            <div className="flex items-center p-1">Manufacturer</div>
            <div className="flex items-center p-1">Model</div>
            <div className="flex items-center p-1">Status</div>
            <div 
              className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
              onClick={() => handleColumnSort('company_name')}
            >
              Company
              {getSortIcon('company_name')}
            </div>
            <div className="flex items-center p-1">Deployment</div>
            {visibleColumns.insurance && (
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
                onClick={() => handleColumnSort('insurance')}
              >
                Insurance
                {getSortIcon('insurance')}
              </div>
            )}
            {visibleColumns.roadtax && (
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
                onClick={() => handleColumnSort('roadtax')}
              >
                Roadtax
                {getSortIcon('roadtax')}
              </div>
            )}
            {visibleColumns.inspection && (
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
                onClick={() => handleColumnSort('inspection')}
              >
                Inspection
                {getSortIcon('inspection')}
              </div>
            )}
            {visibleColumns.brunei && (
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
                onClick={() => handleColumnSort('brunei')}
              >
                Brunei
                {getSortIcon('brunei')}
              </div>
            )}
            {visibleColumns.sabah && (
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
                onClick={() => handleColumnSort('sabah')}
              >
                Sabah
                {getSortIcon('sabah')}
              </div>
            )}
            {visibleColumns.sarawak && (
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-1 rounded"
                onClick={() => handleColumnSort('sarawak')}
              >
                Sarawak
                {getSortIcon('sarawak')}
              </div>
            )}
          </div>

        {sortedVehicles.map((v, index) => {
              const getPermitDate = (type) => {
                return v.permits?.find(p => p.permit_type === type)?.expiry_date || "-";
              };

              return (
                <div key={v.id}>
                  <div
                      className={`items-center p-3 border-b text-sm ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                      style={{ display: "grid", gridTemplateColumns: `40px ${gridTemplate}`, gap: "8px" }}
                  >
                    {/* Row Number */}
                    <div className="text-center text-sm text-gray-500 flex items-center justify-center">
                      {index + 1}.
                    </div>
                    <div className="flex items-center">{v.plate_number}</div>
                    <div className="flex items-center">{v.machinery_no || '-'}</div>
                    <div className="flex items-center">{formatLabel(v.car_type)}</div>
                    <div className="flex items-center">{v.manufacturer || '-'}</div>
                    <div className="flex items-center">{v.model || '-'}</div>
                    <div className="flex items-center">{formatLabel(v.status)}</div>
                    <div className="flex items-center">{formatLabel(v.company_name)}</div>
                    <div className="flex items-center">{v.project?.name || "-"}</div>
                    {visibleColumns.insurance && <div className="flex items-center">{v.insurance?.expiry_date || "-"}</div>}
                    {visibleColumns.roadtax && <div className="flex items-center">{v.roadtax?.expiry_date || "-"}</div>}
                    {visibleColumns.inspection && <div className="flex items-center">{v.inspection?.expiry_date || "-"}</div>}
                    {visibleColumns.brunei && <div className="flex items-center">{getPermitDate("brunei")}</div>}
                    {visibleColumns.sabah && <div className="flex items-center">{getPermitDate("sabah")}</div>}
                    {visibleColumns.sarawak && <div className="flex items-center">{getPermitDate("sarawak")}</div>}

                    {/* Detail + Delete Actions */}
                    <div className="col-span-10 flex justify-end gap-2 mt-1">
                      <a
                          href={`/vehicle/${v.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-blue-600 text-white text-sm px-3 py-1 rounded hover:bg-blue-700"
                      >
                        Details
                      </a>
                      <button
                          className="bg-green-600 text-white text-sm px-3 py-1 rounded hover:bg-green-700 flex items-center gap-1"
                          onClick={() => openQuickUpdateDialog(v)}
                      >
                        <Edit size={14} />
                        Quick Update
                      </button>
                      {isAdmin && (
                          <button
                              className="bg-red-500 text-white text-sm px-3 py-1 rounded hover:bg-red-600"
                              onClick={() => handleDelete(v.id)}
                          >
                            Delete
                          </button>
                      )}
                    </div>

                    {logs[v.id] && (
                        <div className="col-span-10 mt-2">
                          <VehicleLogList
                              logs={logs[v.id]}
                              onClose={() =>
                                  setLogs((prev) => {
                                    const copy = { ...prev };
                                    delete copy[v.id];
                                    return copy;
                                  })
                              }
                          />
                        </div>
                    )}
                  </div>
                </div>
              );
            })}
            </>
          )}
          </div>
        </div>
        
        {/* Dialogs */}
        {/* ⭐ UPDATED Export CSV dialog */}
        {showExportDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded shadow-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
                <h2 className="text-lg font-bold mb-4">Select fields to export</h2>

                {/* Always include Plate Number */}
                <div className="mb-4">
                  <label className="flex items-center space-x-2 font-semibold">
                    <input type="checkbox" checked disabled /> {/* Always ticked */}
                    <span>Plate Number (always included)</span>
                  </label>
                </div>

                {/* Document columns section */}
                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Document Columns</h3>
                  <div className="space-y-1">
                    <label className="flex items-center space-x-2">
                      <input
                          type="checkbox"
                          checked={dialogColumns.insurance}
                          onChange={(e) =>
                              setDialogColumns((prev) => ({
                                ...prev,
                                insurance: e.target.checked,
                              }))
                          }
                      />
                      <span>Insurance</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                          type="checkbox"
                          checked={dialogColumns.roadtax}
                          onChange={(e) =>
                              setDialogColumns((prev) => ({
                                ...prev,
                                roadtax: e.target.checked,
                              }))
                          }
                      />
                      <span>Roadtax</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                          type="checkbox"
                          checked={dialogColumns.inspection}
                          onChange={(e) =>
                              setDialogColumns((prev) => ({
                                ...prev,
                                inspection: e.target.checked,
                              }))
                          }
                      />
                      <span>Inspection</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                          type="checkbox"
                          checked={dialogColumns.brunei}
                          onChange={(e) =>
                              setDialogColumns((prev) => ({
                                ...prev,
                                brunei: e.target.checked,
                              }))
                          }
                      />
                      <span>Brunei Permit (includes Ref No)</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                          type="checkbox"
                          checked={dialogColumns.sabah}
                          onChange={(e) =>
                              setDialogColumns((prev) => ({
                                ...prev,
                                sabah: e.target.checked,
                              }))
                          }
                      />
                      <span>Sabah Permit (includes Ref No)</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                          type="checkbox"
                          checked={dialogColumns.sarawak}
                          onChange={(e) =>
                              setDialogColumns((prev) => ({
                                ...prev,
                                sarawak: e.target.checked,
                              }))
                          }
                      />
                      <span>Sarawak Permit (includes Ref No)</span>
                    </label>
                  </div>
                </div>

                {/* Extra vehicle info fields */}
                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Extra Vehicle Info</h3>
                  <div className="space-y-1">
                    {extraFieldsList.map((field) => (
                        <label key={field.key} className="flex items-center space-x-2">
                          <input
                              type="checkbox"
                              checked={selectedExtras.includes(field.key)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedExtras((prev) => [...prev, field.key]);
                                } else {
                                  setSelectedExtras((prev) =>
                                      prev.filter((k) => k !== field.key)
                                  );
                                }
                              }}
                          />
                          <span>{field.label}</span>
                        </label>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="flex items-center space-x-2 font-semibold mb-2">
                    <input
                        type="checkbox"
                        checked={useDeploymentRange}
                        onChange={(e) => setUseDeploymentRange(e.target.checked)}
                    />
                    <span>Filter by Deployment Date Range</span>
                  </label>

                  {/* Show date pickers only if checked */}
                  {useDeploymentRange && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="text-sm font-medium">From</label>
                          <Input
                              type="date"
                              value={exportRange.from || ""}
                              onChange={(e) =>
                                  setExportRange((prev) => ({ ...prev, from: e.target.value }))
                              }
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">To</label>
                          <Input
                              type="date"
                              value={exportRange.to || ""}
                              onChange={(e) =>
                                  setExportRange((prev) => ({ ...prev, to: e.target.value }))
                              }
                          />
                        </div>
                      </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <button
                      className="px-3 py-1 border rounded"
                      onClick={() => setShowExportDialog(false)}
                  >
                    Cancel
                  </button>
                  <button
                      className="px-3 py-1 bg-blue-500 text-white rounded"
                      onClick={async () => {
                        setShowExportDialog(false);
                        await generateXLSX();
                      }}
                  >
                    Export
                  </button>
                </div>
              </div>
            </div>
        )}

             {/* Quick Update Dialog */}
       {showQuickUpdateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-lg font-bold mb-4">
              Quick Update - {quickUpdateVehicle?.plate_number}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <Select
                  value={quickUpdateData.status}
                  onValueChange={(value) => setQuickUpdateData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Deployment</label>
                <Select
                  value={quickUpdateData.project}
                  onValueChange={(value) => setQuickUpdateData(prev => ({ ...prev, project: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select deployment" />
                  </SelectTrigger>
                  <SelectContent>
                    {deployments.map((deployment) => (
                      <SelectItem key={deployment.id} value={deployment.id.toString()}>
                        {deployment.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowQuickUpdateDialog(false);
                  setQuickUpdateVehicle(null);
                  setQuickUpdateData({ status: "", project: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleQuickUpdate}
                className="bg-green-600 hover:bg-green-700"
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
